import React, { useRef, useState, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { PlayArrow, Pause } from "@mui/icons-material";
import YouTube from "react-youtube"; // Install with `npm install react-youtube`

export const VideoLoopPlayer = ({ src, onError, onEnded }) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null); // Reference for the player container
  const [isPlaying, setIsPlaying] = useState(false); // Video playing state
  const [hasError, setHasError] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showButton, setShowButton] = useState(true); // Button visibility state

  const isYouTube = src.includes("youtube.com") || src.includes("youtu.be");

  const extractVideoId = (src) => {
    const match = src.match(/\/embed\/([^?]+)/);
    return match ? match[1] : null;
  };

  const handleYouTubeEnd = () => {
    if (typeof onEnded === "function") {
      onEnded();
    }
  };

  const handleYouTubeError = () => {
    if (isYouTube) {
      setHasError(true);
      if (typeof onError === "function") {
        onError();
      }
    }
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setShowButton(true); // Show play button when video is paused
      } else {
        videoRef.current.play().catch((err) => {
          console.error("Error playing video:", err);
        });
        setShowButton(false); // Hide button when video starts playing
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleFullScreen = () => {
    if (playerRef.current) {
      if (!isFullScreen) {
        if (playerRef.current.requestFullscreen) {
          playerRef.current.requestFullscreen();
        } else if (playerRef.current.mozRequestFullScreen) {
          playerRef.current.mozRequestFullScreen(); // Firefox
        } else if (playerRef.current.webkitRequestFullscreen) {
          playerRef.current.webkitRequestFullscreen(); // Chrome, Safari
        } else if (playerRef.current.msRequestFullscreen) {
          playerRef.current.msRequestFullscreen(); // IE/Edge
        }
        setIsFullScreen(true);
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen(); // Firefox
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen(); // Chrome, Safari
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen(); // IE/Edge
        }
        setIsFullScreen(false);
      }
    }
  };

  const handleError = () => {
    if (!isYouTube) {
      setHasError(true);
      setIsPlaying(false);
      setShowButton(true); // Show play button on error
      if (onError) onError();
    }
  };

  const handleBoxClick = () => {
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
      setShowButton(true); // Show pause button when video is paused
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    const handleEnded = () => {
      setIsPlaying(false);
      setShowButton(true); // Show play button when video ends
      if (typeof onEnded === "function") {
        onEnded(); // Trigger the parent callback to load the next video
      }
    };

    if (video) {
      video.addEventListener("ended", handleEnded);
      return () => video.removeEventListener("ended", handleEnded);
    }
  }, [onEnded]);

  // Reload the video when the `src` changes, but do not play it automatically
  useEffect(() => {
    const video = videoRef.current;

    if (video) {
      video.setAttribute("src", src);
      video.load(); // Reload the video

      // manual trigger mouse down action
      video
        .play()
        .then(() => {
          setIsPlaying(true); // Ensure the video is in a paused state
          setShowButton(false); // Show the play button
        })
        .catch((err) => {
          setIsPlaying(false); // Ensure the video is in a paused state
          setShowButton(true); // Show the play button
        });
    }
  }, [src]);

  return (
    <Box
      ref={playerRef}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      sx={{
        width: isFullScreen ? "100%" : "100vw",
        height: isFullScreen ? "100%" : "100vh",
        background: "black",
        cursor: isPlaying ? "pointer" : "default",
        position: "relative",
      }}
      onClick={handleBoxClick} // Pause video when box is clicked
      onDoubleClick={toggleFullScreen}
    >
      {!hasError ? (
        <>
          <Box
            sx={{
              width: "100% !important",
              height: "100% !important",
              display: isYouTube ? "block" : "none",
              "& div": {
                width: "100%",
                height: "100%",
              },
            }}
          >
            <YouTube
              videoId={extractVideoId(src)} // Extract video ID from URL
              opts={{
                width: "100%",
                height: "99%",
                playerVars: {
                  autoplay: 1,
                  controls: 0, // Hide player controls
                  fs: 0, // Hide fullscreen button (optional)
                  modestbranding: 1, // Hide YouTube logo
                  rel: 0,
                  disablekb: 1,
                  mute: 0,
                },
              }}
              onEnd={handleYouTubeEnd}
              onError={handleYouTubeError}
            />
          </Box>
          <>
            <video
              onContextMenu={(e) => e.preventDefault()}
              x-webkit-airplay="allow"
              airplay="allow"
              ref={videoRef}
              width="100%"
              height="100%"
              onError={handleError}
              style={{
                background: "black",
                objectFit: "contain",
                display: isYouTube ? "none" : "block",
              }}
            />
            {/* Play/Pause Button */}
            {!isYouTube && showButton && (
              <IconButton
                onClick={togglePlayPause}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                  },
                }}
              >
                {isPlaying ? (
                  <Pause fontSize="large" />
                ) : (
                  <PlayArrow fontSize="large" />
                )}
              </IconButton>
            )}
          </>
        </>
      ) : (
        <Typography color="red">Error loading video</Typography>
      )}
    </Box>
  );
};
